/*  ========================================================================
    # Sections - PartnerSlider
    ========================================================================  */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import LazyImage from '../../components/LazyImage';
import bem from '../../utils/bem';
import useLocalization from '../../hooks/useLocalization';
import useTranslation from '../../hooks/useTranslation';
import { useSwipeable } from 'react-swipeable';

const PartnerSlider = ({ headline, entries }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [translateWidth, setTranslateWidth] = useState(235);
  const __ = useTranslation();
  const ref = useRef();

  // Icons
  const localize = useLocalization();
  const iconChevronLeft = `${localize('svgSprite')}#angle-left`;
  const iconChevronRight = `${localize('svgSprite')}#angle-right`;

  // If there are fewer than 5 entries, the array is padded with existing ones.
  const sliderEntries = useMemo(() => {
    let tempEntries = [];

    if (entries.length < 5) {
      tempEntries = [...entries];
      while (tempEntries.length < 5) tempEntries.push(...entries);
    } else {
      tempEntries = entries;
    }

    return tempEntries;
  }, [entries]);

  // Get the slide offset value.
  const getSlideOffset = index => {
    const delta = index - activeSlide;

    if (activeSlide === 0 && index === sliderEntries.length - 1) return -1;
    if (activeSlide === 0 && index === sliderEntries.length - 2) return -2;
    if (activeSlide === 1 && index === sliderEntries.length - 1) return -2;

    if (activeSlide === sliderEntries.length - 1 && index === 0) return 1;
    if (activeSlide === sliderEntries.length - 1 && index === 1) return 2;

    if (activeSlide === sliderEntries.length - 2 && index === 0) return 2;

    if (delta <= -3) return -2;
    if (delta >= 3) return 2;

    return delta;
  };

  // Navigate Left
  const navigateLeft = () => {
    if (activeSlide > 0) setActiveSlide(activeSlide - 1);
    else setActiveSlide(sliderEntries.length - 1);
  };

  // Navigate Right
  const navigateRight = () => {
    if (activeSlide < sliderEntries.length - 1) setActiveSlide(activeSlide + 1);
    else setActiveSlide(0);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: navigateRight,
    onSwipedRight: navigateLeft
  });

  useEffect(() => {
    const handleResize = () => {
      setTranslateWidth(ref?.current?.offsetWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setTranslateWidth(ref?.current?.offsetWidth);
  }, [ref]);

  return (
    <>
      <div className='container'>
        {headline && <div className='s-partner-slider__headline wysiwyg-block tw-w-full lg:tw-w-10/12 lg:tw-ml-[calc(1/12*100%)]' dangerouslySetInnerHTML={{ __html: headline }}></div>}
      </div>
      <div className='container'>
        <div {...swipeHandlers} className='tw-w-6/12 tw-ml-[calc(3/12*100%)] sm:tw-w-4/12 sm:tw-ml-[calc(4/12*100%)] lg:tw-w-3/12 lg:tw-ml-[calc(9/24*100%)] xl:tw-w-2/12 xl:tw-ml-[calc(5/12*100%)]'>
          <div ref={ref} className={bem('s-partner-slider', 'logo-container')} style={{ '--translation-x': translateWidth + 'px' }}>
            {sliderEntries.map((entry, index) => (
              <div
                key={`${entry.name}-${index}`}
                className='s-partner-slider__logo-slide'
                data-offset={sliderEntries.length === 1 ? '' : getSlideOffset(index)}
                onClick={() => setActiveSlide(index)}
              >
                <LazyImage
                  key={`${entry.name}-${index}`}
                  figureClass='s-partner-slider__logo'
                  defaultUrl={entry.logo.sizes['medium']}
                  alt={entry.logo.alt}
                  placeholderUrl={entry.logo.sizes['placeholder']}
                  dataOffset={0}
                />
              </div>
            ))}
          </div>
        </div>
        <div className='tw-w-full lg:tw-w-10/12 lg:tw-ml-[calc(1/12*100%)]'>
          <div className='s-partner-slider__description-container'>
            {sliderEntries.map((entry, index) => (
              <div
                key={`${entry.name}-${index}`}
                className='s-partner-slider__description-slide wysiwyg-block'
                dangerouslySetInnerHTML={{ __html: entry.description }}
                data-offset={sliderEntries.length === 1 ? '' : getSlideOffset(index)}
              />
            ))}
            <div className='s-partner-slider__slider-controls'>
              <button
                className='s-partner-slider__slider-control c-button c-button--round'
                title={__('section-partner-slider-navigate-left')}
                aria-label={__('section-partner-slider-navigate-left')}
                onClick={navigateLeft}
              >
                <svg viewBox='0 0 200 200'>
                  <use href={iconChevronLeft} />
                </svg>
              </button>
              <button
                className='s-partner-slider__slider-control c-button c-button--round'
                title={__('section-partner-slider-navigate-right')}
                aria-label={__('section-partner-slider-navigate-right')}
                onClick={navigateRight}
              >
                <svg viewBox='0 0 200 200'>
                  <use href={iconChevronRight} />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>

  );
};

PartnerSlider.propTypes = {
  headline: PropTypes.string,
  partners: PropTypes.array
};

export default PartnerSlider;
